import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthGate from './AuthGate';
import AccountHeader from './AccountHeader';
import '../static/css/SettingsPage.css';
import '../static/css/DeleteAccount.css';

class DeleteAccount extends Component {
    constructor() {
        super();
        const userJson = localStorage.getItem('user');
        const user = userJson ? JSON.parse(userJson) : null;
        this.state = {
            firstName: (user && user.first_name) || 'Friend',
            isFoundingMember: !!(user && user.is_founding_member),
            isLegacyPricing: !!(user && user.is_premium_community_only && user.is_legacy_pricing),
            showConfirmModal: false,
            isDeleting: false,
            error: '',
        };
        this.openConfirmModal = this.openConfirmModal.bind(this);
        this.closeConfirmModal = this.closeConfirmModal.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
    }

    openConfirmModal() {
        if (this.state.isDeleting) return;
        this.setState({ showConfirmModal: true, error: '' });
    }

    closeConfirmModal() {
        if (this.state.isDeleting) return;
        this.setState({ showConfirmModal: false });
    }

    async confirmDelete() {
        const token = localStorage.getItem('token');
        if (!token) {
            this.setState({ error: 'Your session has expired. Please sign in again.', showConfirmModal: false });
            return;
        }
        this.setState({ isDeleting: true, error: '' });
        try {
            await axios.delete(
                process.env.REACT_APP_SERVER_API + '/account/delete',
                { headers: { Authorization: 'Bearer ' + token } }
            );
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('upcoming_event');
            window.location.href = '/';
        } catch (err) {
            this.setState({
                isDeleting: false,
                showConfirmModal: false,
                error: 'We could not delete your account. Please try again, or email hello@animates.app if the problem continues.',
            });
        }
    }

    renderConfirmModal() {
        const { isDeleting } = this.state;
        return (
            <div
                className="delete-account-modal-overlay"
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-account-modal-title"
            >
                <div className="delete-account-modal-card">
                    <h3 id="delete-account-modal-title" className="delete-account-modal-title" style={{fontFamily: "'Chelsea Market', cursive"}}>Are you sure?</h3>
                    <p className="delete-account-modal-message">
                        This action is permanent and cannot be undone.
                    </p>
                    <div className="delete-account-modal-actions">
                        <button
                            type="button"
                            className="delete-account-modal-yes"
                            onClick={this.confirmDelete}
                            disabled={isDeleting}
                            autoFocus
                        >
                            {isDeleting ? 'Deleting…' : 'Yes, delete'}
                        </button>
                        <button
                            type="button"
                            className="delete-account-modal-no"
                            onClick={this.closeConfirmModal}
                            disabled={isDeleting}
                        >
                            No, cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderDeletingOverlay() {
        return (
            <div className="delete-account-deleting-overlay" role="status" aria-live="polite">
                <div className="loading-spinner"></div>
                <p className="delete-account-deleting-message">Deleting your account…</p>
            </div>
        );
    }

    render() {
        const { firstName, isFoundingMember, isLegacyPricing, showConfirmModal, isDeleting, error } = this.state;
        const hasSpecialLosses = isFoundingMember || isLegacyPricing;
        return (
            <AuthGate>
                <div className="settings-page">
                    <AccountHeader />
                    <div className="settings-page-content delete-account-content">
                        <Link to="/home" className="settings-back-link">Back</Link>
                        <h2 className="settings-page-title color-1" style={{fontFamily: "'Chelsea Market', cursive"}}><u>Delete Account</u></h2>

                        <div className="delete-account-card">
                            <h3 className="delete-account-heading" style={{fontFamily: "'Chelsea Market', cursive"}}>
                                {firstName}, we're sad to see you go 😔
                            </h3>

                            <p className="delete-account-paragraph">
                                Deleting your account is permanent and cannot be undone.
                            </p>

                            <p className="delete-account-subhead">When you delete your account:</p>
                            <ul className="delete-account-list">
                                <li>Your profile and account information will be permanently removed</li>
                                <li>Your Power Level, stats, and activity history will be erased</li>
                                <li>Your events, meetups, and any content you created will be deleted</li>
                                <li>Your connections, followers, and messages will be removed</li>
                            </ul>

                            {hasSpecialLosses && (
                                <div className="delete-account-special-losses">
                                    <p className="delete-account-special-losses-title">⚠️ You'll also permanently lose:</p>
                                    <ul className="delete-account-special-losses-list">
                                        {isFoundingMember && (
                                            <li className="delete-account-special-loss-item">
                                                <span className="delete-account-badge delete-account-badge-founding" style={{fontFamily: "'Chelsea Market', cursive"}}>
                                                    <span className="delete-account-badge-icon">✨</span>
                                                    Founding Member
                                                </span>
                                                <span className="delete-account-special-loss-text">
                                                    Your founding status — limited to early supporters and not granted to new users.
                                                </span>
                                            </li>
                                        )}
                                        {isLegacyPricing && (
                                            <li className="delete-account-special-loss-item">
                                                <span className="delete-account-badge delete-account-badge-legacy" style={{fontFamily: "'Chelsea Market', cursive"}}>
                                                    <span className="delete-account-badge-icon">💎</span>
                                                    Legacy Pricing
                                                </span>
                                                <span className="delete-account-special-loss-text">
                                                    Your locked-in pricing tier — re-subscribing later will be at current standard rates.
                                                </span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            )}

                            <p className="delete-account-paragraph">
                                You will no longer be able to recover this data or restore your account.
                            </p>

                            <p className="delete-account-paragraph delete-account-pause-hint">
                                If you're not sure, you can take a break instead and come back anytime by <Link to="/pause-account" className="delete-account-link">pausing your account</Link>.
                            </p>

                            {error && <p className="delete-account-error">{error}</p>}

                            <button
                                type="button"
                                className="delete-account-button"
                                onClick={this.openConfirmModal}
                                disabled={isDeleting}
                            >
                                Delete Account
                            </button>
                        </div>
                    </div>
                    {showConfirmModal && this.renderConfirmModal()}
                    {isDeleting && this.renderDeletingOverlay()}
                </div>
            </AuthGate>
        );
    }
}

export default DeleteAccount;
